import { useEffect, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { MobileMenu } from './MobileMenu'
import {
  productMenuFeatured,
  productMenuGroups,
  getProductMenuItem,
} from '@/data/productMenu'

export interface NavItem {
  label: string
  href?: string
  description?: string
  menuHeading?: string
  menuTagline?: string
  /** Marks the item that opens the verticals mega-menu (content-driven from verticals data). */
  mega?: boolean
  children?: NavItem[]
}

interface HeaderProps {
  logoText?: string
  logoSrc?: string
  nav?: NavItem[]
  ctaLabel?: string
  ctaHref?: string
}

/** Prop-driven header. Wire nav from a global content document in RootLayout
 *  (via useEditableContent) so link labels stay CMS-editable. */
export function Header({ logoText = '', logoSrc, nav = [], ctaLabel, ctaHref }: HeaderProps) {
  const [open, setOpen] = useState(false)

  return (
    <header className="sticky top-0 z-40 bg-limestone/90 backdrop-blur border-b border-border">
      <div className="container-content flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg" aria-label={logoText || 'Home'}>
          {logoSrc ? <img src={logoSrc} alt={logoText} className="h-8 w-auto" /> : logoText}
        </Link>

        <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
          {nav.map((item) =>
            item.mega || item.children?.length ? (
              <NavDropdown key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.href}
                to={item.href ?? '#'}
                className={({ isActive }) => (isActive ? 'nav-link-active' : 'nav-link')}
              >
                {item.label}
              </NavLink>
            ),
          )}
          {ctaLabel && ctaHref && (
            <Link to={ctaHref} className="btn-primary btn-sm">
              {ctaLabel}
            </Link>
          )}
        </nav>

        <button
          type="button"
          className="md:hidden p-2 -mr-2"
          aria-expanded={open}
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="block w-6 h-0.5 bg-current mb-1.5" />
          <span className="block w-6 h-0.5 bg-current mb-1.5" />
          <span className="block w-6 h-0.5 bg-current" />
        </button>
      </div>

      <MobileMenu open={open} nav={nav} ctaLabel={ctaLabel} ctaHref={ctaHref} onClose={() => setOpen(false)} />
    </header>
  )
}

/** Desktop-only mega-menu: a full-width, hero-scale panel listing every vertical
 *  grouped by category on the left and a representative image of the hovered
 *  vertical on the right. Opens on hover and on click/keyboard focus; a short
 *  close delay bridges the gap between the trigger and the panel so moving the
 *  pointer onto the menu never dismisses it. Closes on outside click, Escape,
 *  or selection. */
function NavDropdown({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false)
  const [activeSlug, setActiveSlug] = useState(productMenuFeatured.slug)
  const ref = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()

  const active = getProductMenuItem(activeSlug)

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpen(true)
  }
  // Delay closing so the pointer can travel from the trigger, across the sticky
  // header's padding, onto the fixed panel without the menu snapping shut.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpen(false), 160)
  }
  const close = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current) }, [])

  return (
    <div ref={ref} onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        type="button"
        className={`nav-link inline-flex items-center gap-1 ${open ? 'text-copper' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {item.label}
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Full-width hero-scale mega panel, anchored below the sticky header.
          onMouseEnter/Leave also live here (the panel is a DOM descendant) so
          hovering the panel keeps it open and leaving it schedules the close. */}
      <div
        role="menu"
        aria-label={item.label}
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        className={`fixed left-0 right-0 top-16 z-30 border-b border-white/10 bg-slate shadow-2xl transition-all duration-200 ${
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="container-content py-10 lg:py-12 grid gap-10 lg:grid-cols-[1.35fr_1fr] items-stretch">
          {/* Left — categorised vertical list */}
          <div className="flex flex-col">
            <div className="mb-5">
              <span className="section-eyebrow text-copper-light">
                {item.menuHeading ?? item.label}
              </span>
              {item.menuTagline && (
                <p className="mt-2 font-serif italic text-limestone/60 text-base max-w-md">
                  {item.menuTagline}
                </p>
              )}
            </div>

            {/* Featured, live vertical */}
            <NavLink
              role="menuitem"
              to={productMenuFeatured.href}
              onMouseEnter={() => setActiveSlug(productMenuFeatured.slug)}
              onFocus={() => setActiveSlug(productMenuFeatured.slug)}
              onClick={close}
              className={`group mb-5 flex items-center justify-between gap-4 rounded-xl border px-5 py-3.5 transition-all duration-150 ${
                activeSlug === productMenuFeatured.slug
                  ? 'border-copper/60 bg-copper/10'
                  : 'border-white/10 hover:bg-limestone/5'
              }`}
            >
              <span>
                <span className="block font-heading font-semibold text-lg text-limestone">
                  {productMenuFeatured.title}
                </span>
                <span className="mt-0.5 block font-serif text-sm text-limestone/55">
                  Backtestable satellite crop signals — point-in-time and desk-ready.
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-copper/20 px-2.5 py-1 font-heading text-[0.7rem] font-semibold uppercase tracking-wide text-copper-light">
                {productMenuFeatured.statusLabel}
              </span>
            </NavLink>

            {/* Categorised, upcoming verticals */}
            <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
              {productMenuGroups.map((group) => (
                <div key={group.id}>
                  <p className="section-eyebrow text-limestone/40 mb-2">{group.title}</p>
                  <ul className="flex flex-col gap-0.5">
                    {group.items.map((entry) => {
                      const isActive = entry.slug === activeSlug
                      return (
                        <li key={entry.slug} role="none">
                          <NavLink
                            role="menuitem"
                            to={entry.href}
                            onMouseEnter={() => setActiveSlug(entry.slug)}
                            onFocus={() => setActiveSlug(entry.slug)}
                            onClick={close}
                            className={`group flex items-center gap-2 rounded-lg px-3 py-1.5 font-heading text-sm transition-colors duration-150 ${
                              isActive ? 'bg-limestone/10 text-limestone' : 'text-limestone/70 hover:text-limestone'
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 shrink-0 rounded-full transition-colors ${
                                isActive ? 'bg-copper' : 'bg-limestone/25'
                              }`}
                              aria-hidden="true"
                            />
                            {entry.title}
                          </NavLink>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Right — representative image of the hovered vertical */}
          <Link
            to={active.href}
            onClick={close}
            tabIndex={-1}
            aria-hidden="true"
            className="relative hidden lg:block overflow-hidden rounded-2xl min-h-[18rem] group"
          >
            <img
              key={active.slug}
              src={active.image}
              alt={active.imageAlt}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate/85 via-slate/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="section-eyebrow text-copper-light">{active.statusLabel}</span>
              <p className="mt-1 font-heading font-bold text-limestone text-xl">
                {active.title}
              </p>
            </div>
          </Link>
        </div>

        <div className="border-t border-white/10">
          <div className="container-content py-3">
            <NavLink
              to="/verticals"
              onClick={close}
              className="inline-flex items-center gap-1.5 font-heading font-semibold text-sm text-copper-light hover:text-limestone transition-colors"
            >
              Explore all verticals — same engine, different questions
              <span aria-hidden="true">→</span>
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  )
}
